// novem atom — copy-to-clipboard button (NovemHR-ekranlar port).
//
// B3 (licenses issue modal + license detail) wrote the same
// "copy the signed blob → flip to a check + 'Copied' label → reset"
// affordance twice by hand. This promotes it with the missing piece the
// screen-local copies lacked: a screen-reader announcement when the copy
// succeeds (the visual check alone is silent to AT).
//
// API (per the call sites' real usage):
//   * `value` — the string to copy (the common case), OR
//   * `getText()` — a lazy provider when the text is computed on click.
//   One of the two is required (dev-guard asserts).
//   * `copiedLabel` — what to show + announce after a successful copy.
//   * `onCopy` — optional override of the copy mechanism (defaults to
//     navigator.clipboard.writeText). Lets non-clipboard hosts (and
//     tests) inject their own; the default degrades gracefully when the
//     Clipboard API is unavailable.
//
// Icon-agnostic: idle/copied glyphs are SLOTS (host passes lucide). They
// are decorative (aria-hidden) — the accessible name is the text label,
// and the live region announces the state change.
//
// Accessibility:
//   * Native <button type="button">.
//   * A visually-hidden aria-live="polite" region announces copiedLabel
//     on success so the state change is not check-icon-only.
//   * The copied state auto-resets after `resetMs` (default 2000ms).

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";
import type { ButtonSize, ButtonVariant } from "../Button/Button.tsx";

export interface CopyButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "value" | "children" | "onCopy"> {
  /** Text to copy (static case). Provide this OR getText. */
  readonly value?: string;
  /** Lazy provider for the text (computed-on-click case). Wins over value. */
  readonly getText?: () => string;
  /** Idle label (e.g. t("...copy")). */
  readonly label: ReactNode;
  /** Label shown + announced after a successful copy (e.g. t("...copied")). */
  readonly copiedLabel: ReactNode;
  /** Decorative idle glyph slot (host passes a lucide icon). */
  readonly idleIcon?: ReactNode;
  /** Decorative copied glyph slot. Falls back to idleIcon when omitted. */
  readonly copiedIcon?: ReactNode;
  /** Button variant hook (reuses Button's skin). Defaults to 'secondary'. */
  readonly variant?: ButtonVariant;
  /** Button size hook. Defaults to 'md'. */
  readonly size?: ButtonSize;
  /** Override the copy mechanism. Defaults to navigator.clipboard.writeText. */
  readonly onCopy?: (text: string) => void | Promise<void>;
  /** Milliseconds before the copied state resets. Defaults to 2000. */
  readonly resetMs?: number;
}

async function defaultCopy(text: string): Promise<void> {
  // Guard for environments without the async Clipboard API (older
  // browsers, insecure contexts). The host can always inject onCopy.
  if (typeof navigator !== "undefined" && navigator.clipboard !== undefined) {
    await navigator.clipboard.writeText(text);
  }
}

export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      value,
      getText,
      label,
      copiedLabel,
      idleIcon,
      copiedIcon,
      variant = "secondary",
      size = "md",
      onCopy,
      resetMs = 2000,
      className,
      onClick,
      ...rest
    },
    ref,
  ) => {
    if (isDevGuardActive() && value === undefined && getText === undefined) {
      throw new Error("<CopyButton> requires either `value` or `getText`.");
    }

    const [copied, setCopied] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Clear any pending reset timer on unmount.
    useEffect(
      () => (): void => {
        if (timer.current !== undefined) clearTimeout(timer.current);
      },
      [],
    );

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        const text = getText !== undefined ? getText() : (value ?? "");
        const run = onCopy ?? defaultCopy;
        void Promise.resolve(run(text)).then((): void => {
          setCopied(true);
          if (timer.current !== undefined) clearTimeout(timer.current);
          timer.current = setTimeout((): void => setCopied(false), resetMs);
        });
      },
      [getText, value, onCopy, onClick, resetMs],
    );

    const glyph = copied ? (copiedIcon ?? idleIcon) : idleIcon;

    return (
      <button
        ref={ref}
        type="button"
        data-component="copy-button"
        data-variant={variant}
        data-size={size}
        data-copied={copied || undefined}
        className={cx("nai-btn", "nai-copy-button", className)}
        onClick={handleClick}
        {...rest}
      >
        {glyph !== undefined && (
          <span className="nai-copy-button-icon" aria-hidden="true">
            {glyph}
          </span>
        )}
        <span>{copied ? copiedLabel : label}</span>
        {/* SR announcement — the visual check is silent to AT otherwise. */}
        <span className="nai-sr-only" role="status" aria-live="polite">
          {copied ? copiedLabel : ""}
        </span>
      </button>
    );
  },
);
CopyButton.displayName = "CopyButton";
